'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { tv } from 'tailwind-variants';

import { cn } from '@/utils/cn-tw-merge';

interface TabsOffset {
  [ket: string]: {
    width: number;
    offset: number;
  };
}

const SecondaryTabsContext = React.createContext<{
  tabs: TabsOffset;
  activeTab: string;
  setTabs: React.Dispatch<React.SetStateAction<TabsOffset>>;
  setActiveTab: (tab: string) => void;
} | null>(null);

export const useTabs = () => {
  const context = React.useContext(SecondaryTabsContext);
  if (!context) {
    throw new Error('useTabs must be used within a SecondaryTabProvider');
  }
  return context;
};

const SecondaryTabProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeTab, setActiveTab] = React.useState<string>('');
  const [tabs, setTabs] = React.useState<TabsOffset>({});

  return (
    <SecondaryTabsContext.Provider value={{ activeTab, tabs, setActiveTab, setTabs }}>
      {children}
    </SecondaryTabsContext.Provider>
  );
};

const SecondaryTabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ ...props }, ref) => {
  const { setActiveTab } = useTabs();

  React.useEffect(() => {
    setActiveTab(props.defaultValue || '');
  }, []);

  React.useEffect(() => {
    if (props.value) {
      setActiveTab(props.value || '');
    }
  }, [props?.value]);

  return (
    <TabsPrimitive.Root
      ref={ref}
      onValueChange={(value) => {
        props?.onValueChange?.(value);
        setActiveTab(value);
      }}
      {...props}
    />
  );
});

SecondaryTabs.displayName = TabsPrimitive.Root.displayName;

const SecondaryTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  return (
    <TabsPrimitive.List ref={ref} className={cn('w-full z-50', className)} {...props}>
      <div className="relative w-full flex justify-around">
        <div className="absolute bottom-0 left-0 bg-gray-200 rounded w-full h-0.5" />
        {props.children}
      </div>
    </TabsPrimitive.List>
  );
});
SecondaryTabsList.displayName = TabsPrimitive.List.displayName;

const secondaryTabTrigger = tv({
  slots: {
    container: ['w-full flex flex-col items-center justify-around relative cursor-pointer'],
    bar: ['w-0 opacity-0 h-0.5 bg-gray-700 rounded', 'absolute bottom-0 transition-all duration-300'],
    buttonTrigger: ['text-gray-700 text-center flex-1 flex items-center justify-center'],
  },

  variants: {
    active: {
      true: {
        buttonTrigger: ['text-gray-900 font-bold w-full'],
        bar: ['w-full opacity-100'],
      },
    },
  },
});

const SecondaryTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { setTabs, activeTab } = useTabs();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const { bar, buttonTrigger, container } = secondaryTabTrigger();

  const isActive = activeTab === props.value;

  React.useEffect(() => {
    setTabs((prev) => {
      return {
        ...prev,
        [props.value]: {
          width: (ref && 'current' in ref ? ref.current?.offsetWidth : triggerRef.current?.offsetWidth) || 0,
          offset: (ref && 'current' in ref ? ref.current?.offsetLeft : triggerRef.current?.offsetLeft) || 0,
        },
      };
    });
  }, [props.value, ref, triggerRef]);

  return (
    <TabsPrimitive.Trigger ref={ref || triggerRef} className={container({ className })} {...props}>
      <span className={buttonTrigger({ active: isActive })}>{props.children}</span>
      <div className={bar({ active: isActive })} />
    </TabsPrimitive.Trigger>
  );
});
SecondaryTabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const SecondaryTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 dark:ring-offset-slate-950 dark:focus-visible:ring-slate-300',
      className,
    )}
    {...props}
  />
));
SecondaryTabsContent.displayName = TabsPrimitive.Content.displayName;

export { SecondaryTabs, SecondaryTabsList, SecondaryTabsTrigger, SecondaryTabsContent, SecondaryTabProvider };
